import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Typography,
} from "@mui/material";
import { Input, Select } from "../../controls";
import { Form } from "../../layout";
import { Model, UseForm } from "../../hooks";

import { connect } from "react-redux";
import * as actions from "../../api/actions/pos";
import { endpoints } from "../../api/actions/api";


const AddEditStorePrinter = (props) => {
  const { t } = useTranslation();
  const { updData, setShowForm, storeId, _post, _put, refreshPrinters, } = props;

  const { StorePrinterMdl } = Model();

  const {
    values,
    setValues,
    errors,
    setErrors,
    handleInpChg,
    resetFormControls,
  } = UseForm(StorePrinterMdl);

  const validateForm = () => {
    let temp = {};

    temp.printerName =
      values.printerName?.trim()
        ? ""
        : t("printerNameRequired");

    temp.printerType =
      values.printerType !== ""
        ? ""
        : t("printerTypeRequired");

    temp.paperWidth =
      values.paperWidth !== ""
        ? ""
        : t("paperWidthRequired");

    temp.connectionType =
      values.connectionType !== ""
        ? ""
        : t("connectionTypeRequired");

    temp.ipAddress =
      Number(values.connectionType) === 2 &&
      !values.ipAddress?.trim()
        ? t("networkIpRequired")
        : "";

    temp.port =
      values.port !== "" &&
      values.port !== null &&
      (Number(values.port) < 1 || Number(values.port) > 65535)
        ? t("printerPortInvalid")
        : "";

    setErrors({ ...temp });

    return Object.values(temp).every((x) => x === "");
  };

  const handleCheckChg = (e) => {
    const { name, checked } = e.target;

    setValues({
        ...values,
        [name]: checked,
    });
    };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    const isEdit = updData !== undefined && updData?.id > 0;

    const printerData = {
        ...values,
        id: isEdit ? Number(values.id) : 0,
        storeId: Number(storeId),
        printerType: Number(values.printerType),
        paperWidth: Number(values.paperWidth),
        connectionType: Number(values.connectionType),

        port:
        values.port === "" || values.port === null
            ? null
            : Number(values.port),

        ipAddress:
        Number(values.connectionType) === 2
            ? values.ipAddress
            : null,
    };

    const onComplete = (res, err) => {
        if (err) {
        console.error(
            isEdit ? "Update printer error:" : "Add printer error:",
            err
        );
        return;
        }

        if (
        (!isEdit && res?.status === 201) ||
        (isEdit && res?.status === 200)
        ) {
        refreshPrinters();
        setShowForm(false);
        }
    };

    if (isEdit) {
        _put(
        `${endpoints.StorePrinter}/${printerData.id}`,
        printerData,
        null,
        onComplete
        );
    } else {
        _post(
        endpoints.StorePrinter,
        printerData,
        null,
        onComplete
        );
    }
    };

  useEffect(() => {
    if (updData) {
      setValues({
        ...StorePrinterMdl(),
        ...updData,
        storeId: Number(storeId),
      });
    } else {
      setValues({
        ...StorePrinterMdl(),
        storeId: Number(storeId),
      });
    }

    return () => {
      resetFormControls();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updData, storeId]);

  return (
    <Form noValidate onSubmit={handleSubmit}>
      <Grid container spacing={2} className="p-20 pt-40">
        <Grid item xs={12} className="tx-center">
          <Typography
            variant="h5"
            className="fs-22 gray-color fw-5 tx-upp"
          >
            {updData === undefined ? t("addNewPrinter") : t("updatePrinter")}
          </Typography>

          <div className="bs-bottom mx-w-80 m-auto mt-10 mb-30" />
        </Grid>

        {/* Printer Name */}
        <Grid item xs={12}>
          <Input
            label={t("printerName") + " *"}
            name="printerName"
            value={values.printerName}
            error={errors.printerName}
            onChange={handleInpChg}
            fullWidth
          />
        </Grid>

        {/* Printer Type */}
        <Grid item xs={12} md={6}>
          <Select
            label={t("printerType") + " *"}
            name="printerType"
            value={values.printerType}
            error={errors.printerType}
            onChange={handleInpChg}
            fullWidth
            options={[
              { id: 1, name: t("posReceipt") },
              { id: 2, name: t("reports") },
              { id: 3, name: t("barcodeLabel") },
            ]}
          />
        </Grid>

        {/* Paper Width */}
        <Grid item xs={12} md={6}>
          <Select
            label={t("paperWidth") + " *"}
            name="paperWidth"
            value={values.paperWidth}
            error={errors.paperWidth}
            onChange={handleInpChg}
            fullWidth
            options={[
              { id: 80, name: "80 mm" },
              { id: 58, name: "58 mm" },
            ]}
          />
        </Grid>

        {/* Driver */}
        <Grid item xs={12}>
          <Input
            label={t("driverName")}
            name="driverName"
            value={values.driverName}
            error={errors.driverName}
            onChange={handleInpChg}
            fullWidth
          />
        </Grid>

        {/* Connection */}
        <Grid item xs={12} md={6}>
          <Select
            label={t("connectionType") + " *"}
            name="connectionType"
            value={values.connectionType}
            error={errors.connectionType}
            onChange={handleInpChg}
            fullWidth
            options={[
              { id: 1, name: t("windowsUsb") },
              { id: 2, name: t("network") },
            ]}
          />
        </Grid>

        {/* Port */}
        <Grid item xs={12} md={6}>
          <Input
            label={t("port")}
            name="port"
            type="number"
            value={values.port}
            error={errors.port}
            onChange={handleInpChg}
            fullWidth
          />
        </Grid>

        {/* IP - only useful for Network */}
        {Number(values.connectionType) === 2 && (
          <Grid item xs={12}>
            <Input
              label={t("ipAddress") + " *"}
              name="ipAddress"
              value={values.ipAddress}
              error={errors.ipAddress}
              onChange={handleInpChg}
              fullWidth
            />
          </Grid>
        )}

        {/* Settings */}
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                name="isDefault"
                checked={Boolean(values.isDefault)}
                onChange={handleCheckChg}
              />
            }
            label={t("defaultPrinter")}
          />

          <FormControlLabel
            control={
              <Checkbox
                name="isActive"
                checked={Boolean(values.isActive)}
                onChange={handleCheckChg}
              />
            }
            label={t("active")}
          />

          <FormControlLabel
            control={
              <Checkbox
                name="autoPrint"
                checked={Boolean(values.autoPrint)}
                onChange={handleCheckChg}
              />
            }
            label={t("autoPrint")}
          />
        </Grid>

        {/* Buttons */}
        <Grid
          item
          xs={12}
          className="tx-right d-flex mt-20"
          sx={{ flexWrap: { xs: "wrap", md: "initial" } }}
        >
          <Button
            type="submit"
            className="btn-primary w-100pr"
            size="large"
          >
            {updData === undefined ? t("submit") : t("saveChanges")}
          </Button>

          <Button
            onClick={() => setShowForm(false)}
            className="btn-error w-100pr"
            size="large"
            sx={{
              ml: { md: "10px" },
              mt: { xs: "15px", md: "0px" },
            }}
          >
            {t("close")}
          </Button>
        </Grid>
      </Grid>
    </Form>
  );
};


const mapActionToProps = {
  _post: actions.post,
  _put: actions.put,
};

export default connect(
  null,
  mapActionToProps
)(AddEditStorePrinter);